import React, { useEffect } from 'react'
import GModal from 'tennders-market/common/components/element/GModal'
import MultipleSalesInvoiceGeneralInfo from 'tennders-finance/sales-invoice/components/multiple-sales-invoice/MultipleSalesInvoiceGeneralInfo'
import MultipleSalesInvoiceLines from 'tennders-finance/sales-invoice/components/multiple-sales-invoice/MultipleSalesInvoiceLines'
import useMultipleLineForm from 'tennders-finance/cost-invoice/hooks/useMultipleLineForm'
import useTranslation from 'next-translate/useTranslation'
import MultipleSalesInvoiceExtraCostsTable from 'tennders-finance/sales-invoice/components/multiple-sales-invoice/MultipleSalesInvoiceExtraCostsTable'
import MultipleSalesInvoiceDiscountsTable from 'tennders-finance/sales-invoice/components/multiple-sales-invoice/MultipleSalesInvoiceDiscountsTable'
import MultipleSalesInvoiceOutput from 'tennders-finance/sales-invoice/components/multiple-sales-invoice/MultipleSalesInvoiceOutput'
import MultipleSalesInvoiceSaveButton from 'tennders-finance/sales-invoice/components/multiple-sales-invoice/MultipleSalesInvoiceSaveButton'
import useMultipleSalesInvoiceForm from 'tennders-finance/sales-invoice/hooks/useMultipleSalesInvoiceForm'
import useMultipleSalesInvoiceCalculateAmounts from 'tennders-finance/sales-invoice/hooks/useMultipleSalesInvoiceCalculateAmounts'
import PubSub from 'pubsub-js'
import { apiGet } from 'tennders-stracture/libs/api'
import useMultipleSalesInvoicePaymentTerm from 'tennders-finance/sales-invoice/hooks/useMultipleSalesInvoicePaymentTerm'
import moment from 'moment'

const SalesInvoiceModal = ({ initialForm, selectedTransaction, isOpen, onClose, ...rest }) => {
  const realInitForm = initialForm?.oplog ? {
    ...initialForm.oplog,
    status: initialForm.status,
    approveEnabled: initialForm.approveEnabled,
    id: initialForm.id
  } : initialForm
  const isCreditNote = initialForm?.objectType === 'creditNote'

  const {
    onChange,
    setForm,
    company,
    form,
    errors: formErrors,
    setErrors: setFormErrors,
    onCompanyChange,
    onDateOfReceiptChange,
    calculatesDueDate,
    getCompanySalesInvoicesRequest,
    companyInvoices
  } = useMultipleSalesInvoiceForm({ initialForm: realInitForm, isCreditNote, selectedTransaction })

  const { t } = useTranslation()

  const {
    errors: lineErrors,
    setLines,
    setErrors: setLineErrors,
    lines,
    removeLine,
    onFormChange,
    addNewLine
  } = useMultipleLineForm(realInitForm?.lines || undefined)

  const {
    errors: discountErrors,
    setLines: setDiscountLines,
    lines: discountLines,
    removeLine: removeDiscountLine,
    onFormChange: onDiscountFormChange,
    addNewLine: onNewAddDiscountLine
  } = useMultipleLineForm()

  const {
    errors: extraCostsErrors,
    setLines: setExtraCostLines,
    lines: extraCostsLines,
    removeLine: removeExtraCostsLine,
    onFormChange: onExtraCostsFormChange,
    addNewLine: onNewAddExtraCostsLine
  } = useMultipleLineForm()

  const {
    totalExtraCosts,
    totalExtraCostsIncVat,
    totalAmountOnInvoice,
    calculateTotalDiscount,
    totalEndAmountToPay,
    totalDiscount,
    totalAmountOnInvoiceIncVat,
    totalVatAmount
  } = useMultipleSalesInvoiceCalculateAmounts({
    form,
    lines,
    discountLines,
    extraCostsLines
  })

  const {
    onPaymentTermChange,
    allPaymentTerms,
    selectedPaymentTerm,
    paymentTermProblem
  } = useMultipleSalesInvoicePaymentTerm({
    calculatesDueDate,
    lines,
    form,
    onChange,
    company
  })

  const publishLines = (key, lines) => {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      PubSub.publish(
        `${key}:${line.id}:change`,
        line
      )
    }
  }

  const initAll = (initialForm) => {
    setForm({ ...initialForm })
    setLines(initialForm.lines)
    setTimeout(() => {
      publishLines('multiple-sales-invoice', initialForm.lines)
    }, 100)

    if (initialForm.discountLines) {
      setDiscountLines(initialForm.discountLines)
      setTimeout(() => {
        publishLines('multiple-sales-invoice-discount', initialForm.discountLines)
      }, 100)
    }

    if (initialForm.extraCostsLines) {
      setExtraCostLines(initialForm.extraCostsLines)
      setTimeout(() => {
        publishLines('multiple-sales-invoice-extra-cost', initialForm.extraCostsLines)
      }, 100)
    }
  }

  useEffect(() => {
    if (initialForm) {
      if (initialForm.oplog) {
        initAll({
          ...initialForm.oplog,
          status: initialForm.status,
          approveEnabled: initialForm.approveEnabled,
          id: initialForm.id
        })
      } else {
        setForm({ ...initialForm })
      }
      if (initialForm.id && initialForm.toCompany) {
        onCompanyChange(initialForm.toCompany)
      }
    }
  }, [initialForm])

  const noSalesInvoice = !form?.salesInvoiceId || form?.salesInvoiceId === 'false'

  useEffect(() => {
    if (!noSalesInvoice && !initialForm.id) {
      apiGet('/salesInvoice/' + form?.salesInvoiceId).then(response => {
        initAll({
          ...response.oplog,
          toCompany: { ...response.toCompany },
          salesInvoiceId: form?.salesInvoiceId,
          date: new Date(moment().format('YYYY-MM-DD')),
          approveEnabled: null,
          id: null,
          status: null
        })
      })
    }
  }, [form?.salesInvoiceId])

  useEffect(() => {
    if (company && !selectedTransaction && !initialForm.date) {
      apiGet('/salesInvoice/initializeWithCompany/' + company.id).then(response => {
        setForm({ ...response, company: company?.id })
      })
    }
  }, [JSON.stringify(company)])

  return (
    isOpen ? (
      <GModal isOpen close={onClose} size="80%" {...rest}>
        <div className="w-100">
          <div className="d-flex column mb-16">
            {isCreditNote ? (
              <h3 className="bold mb-0"> {t('creditNote:creditNote',
                { creditNoteId: initialForm.id || '' })}
              </h3>
            ) : <h3 className="bold mb-0"> {t('salesInvoice:generateSalesInvoice')}</h3>}
          </div>
          <div className="hr" />
          <MultipleSalesInvoiceGeneralInfo
            form={form}
            setForm={setForm}
            allPaymentTerms={allPaymentTerms}
            isCreditNote={isCreditNote}
            formErrors={formErrors}
            onCompanyChange={onCompanyChange}
            onChange={onChange}
            companyInvoices={companyInvoices}
            onDateOfReceiptChange={onDateOfReceiptChange}
            onPaymentTermChange={onPaymentTermChange}
            selectedPaymentTerm={selectedPaymentTerm}
            selectedTransaction={selectedTransaction}
            company={company || form.toCompany}
            getCompanySalesInvoicesRequest={getCompanySalesInvoicesRequest}
          />
          <div className="p-20">
            {(!isCreditNote || !noSalesInvoice) && (
              <>
                <div className="hr" />
                <MultipleSalesInvoiceLines
                  defaultVat={form?.freightVatPercentage}
                  removeLine={removeLine}
                  form={form}
                  selectedPaymentTerm={selectedPaymentTerm}
                  isCreditNote={isCreditNote}
                  editMode={initialForm.id}
                  onFormChange={onFormChange}
                  addNewLine={addNewLine}
                  lines={lines}
                  selectedTransaction={selectedTransaction}
                  errors={lineErrors}
                />
                <MultipleSalesInvoiceExtraCostsTable
                  defaultVat={form?.freightVatPercentage}
                  reversedVat={form?.reversedVat}
                  removeExtraCostsLine={removeExtraCostsLine}
                  extraCostsErrors={extraCostsErrors}
                  extraCostsLines={extraCostsLines}
                  isCreditNote={isCreditNote}
                  companyId={form.company || form?.toCompany?.id}
                  onExtraCostsFormChange={onExtraCostsFormChange}
                  onNewAddExtraCostsLine={onNewAddExtraCostsLine}
                />
                <MultipleSalesInvoiceDiscountsTable
                  editMode={!!form.id}
                  defaultVat={form?.freightVatPercentage}
                  reversedVat={form?.reversedVat}
                  selectedPaymentTerm={selectedPaymentTerm}
                  isCreditNote={isCreditNote}
                  discountErrors={discountErrors}
                  onDiscountFormChange={onDiscountFormChange}
                  totalAmountOnInvoice={totalAmountOnInvoice}
                  totalExtraCosts={totalExtraCosts}
                  discountLines={discountLines}
                  companyId={form.company}
                  onNewAddDiscountLine={onNewAddDiscountLine}
                  removeDiscountLine={removeDiscountLine}
                  lines={lines}
                />
                <div className="d-flex row justify-end w-100">
                  <MultipleSalesInvoiceOutput
                    {...{
                      totalExtraCosts,
                      totalExtraCostsIncVat,
                      totalAmountOnInvoice,
                      totalEndAmountToPay,
                      totalDiscount,
                      totalAmountOnInvoiceIncVat,
                      calculateTotalDiscount,
                      totalVatAmount
                    }}
                  />
                </div>
              </>
            )}
            <div className="hr"></div>
            <MultipleSalesInvoiceSaveButton
              isCreditNote={isCreditNote}
              lines={lines}
              form={form}
              paymentTermProblem={paymentTermProblem}
              setform={setForm}
              selectedTransaction={selectedTransaction}
              calculations={{
                totalExtraCosts,
                totalDiscount,
                totalExtraCostsIncVat,
                totalAmountOnInvoice,
                totalEndAmountToPay,
                totalAmountOnInvoiceIncVat,
                calculateTotalDiscount,
                totalVatAmount
              }}
              setLineForm={onFormChange}
              discountLines={discountLines}
              totalExtraCostsIncVat={totalExtraCostsIncVat}
              totalDiscount={totalDiscount}
              extraCostsLines={extraCostsLines}
              setFormErrors={setFormErrors}
              setLineErrors={setLineErrors}
              onClose={onClose}
            />
          </div>
        </div>
      </GModal>
    ) : null
  )
}

export default React.memo(SalesInvoiceModal)
